import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { SONGWRITER_PLANS, CHURCH_PLANS } from '../lib/plans'
import { Shell } from '../components/Shell'
import { Button, Sill, Check } from '../components/ui'

/**
 * Two independent plans can sit on one account: a songwriter's own, and — if
 * they're a church admin — their church's. Each is its own subscriptions row
 * (one keyed by user_id, one by church_id), so upgrading one never touches
 * the other.
 */
function PlanCard({ title, plans, current, status, busy, onUpgrade, onPause, onResume, onCancel }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div className="eyebrow">{title}</div>
      {status === 'paused' && (
        <p className="small" style={{ marginBottom: 10 }}>
          Paused — Plus features are off until you resume.
        </p>
      )}
      {plans.map((p) => {
        const isCurrent = p.id === current
        return (
          <div key={p.id} className="tile" style={{ marginTop: 10 }}>
            <div className="row">
              <h3 style={{ margin: 0 }}>{p.label}</h3>
              {isCurrent && status !== 'paused' && <span className="pill ok">Current plan</span>}
              {isCurrent && status === 'paused' && <span className="pill">Paused</span>}
            </div>
            <p className="small" style={{ marginTop: 4 }}>{p.blurb}</p>
            <div style={{ marginTop: 8 }}>
              {p.features.map((f) => (
                <div key={f} className="trustrow">
                  <span className="tick"><Check size={11} w={3} /></span>
                  <span className="tt">{f}</span>
                </div>
              ))}
            </div>
            <p className="small" style={{ marginTop: 8, fontWeight: 600 }}>{p.price}</p>

            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!isCurrent && p.id !== 'free' && (
                <Button busy={busy} onClick={() => onUpgrade(p.id)}>Upgrade to {p.label}</Button>
              )}
              {isCurrent && p.id !== 'free' && status === 'active' && (
                <>
                  <button className="btn btn-quiet" disabled={busy} onClick={onPause}>Pause</button>
                  <button className="btn btn-quiet" disabled={busy} onClick={onCancel}>Cancel</button>
                </>
              )}
              {isCurrent && p.id !== 'free' && status === 'paused' && (
                <>
                  <Button busy={busy} onClick={onResume}>Resume</Button>
                  <button className="btn btn-quiet" disabled={busy} onClick={onCancel}>Cancel</button>
                </>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function Plans() {
  const { refresh, tier: userTier, subscription, isAdmin, church } = useSession()
  const nav = useNavigate()
  const [churchSub, setChurchSub] = useState(undefined) // undefined = loading, null = none
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (!isAdmin || !church) { setChurchSub(null); return }
    supabase.from('subscriptions').select('*').eq('church_id', church.id).maybeSingle()
      .then(({ data }) => setChurchSub(data ?? null))
  }, [isAdmin, church])

  const churchTier = churchSub && ['active', 'past_due'].includes(churchSub.status)
    ? churchSub.tier : 'free'

  const run = async (fn) => {
    setBusy(true); setErr(null)
    try {
      await fn()
      await refresh()
      if (isAdmin && church) {
        const { data } = await supabase.from('subscriptions').select('*')
          .eq('church_id', church.id).maybeSingle()
        setChurchSub(data ?? null)
      }
    } catch (e) { setErr(readableError(e)) } finally { setBusy(false) }
  }

  return (
    <Shell tabs>
      <button className="backlink" onClick={() => nav('/account')}>‹ Account</button>
      <h2 className="q">Plans</h2>
      <p className="sub">
        Prices are still being finalised — nothing here charges you yet. Upgrading
        turns the features on now; billing follows once a provider is wired up.
      </p>
      <Sill />

      <PlanCard
        title="Your plan"
        plans={SONGWRITER_PLANS}
        current={userTier}
        status={subscription?.status ?? 'active'}
        busy={busy}
        onUpgrade={(id) => run(() => supabase.rpc('upgrade_subscription', { p_tier: id }))}
        onPause={() => run(() => supabase.rpc('pause_subscription'))}
        onResume={() => run(() => supabase.rpc('resume_subscription'))}
        onCancel={() => run(() => supabase.rpc('cancel_subscription'))}
      />

      {isAdmin && church && churchSub !== undefined && (
        <PlanCard
          title={`${church.name}'s plan`}
          plans={CHURCH_PLANS}
          current={churchTier}
          status={churchSub?.status ?? 'active'}
          busy={busy}
          onUpgrade={(id) => run(() => supabase.rpc('upgrade_subscription', { p_tier: id, p_church: church.id }))}
          onPause={() => run(() => supabase.rpc('pause_subscription', { p_church: church.id }))}
          onResume={() => run(() => supabase.rpc('resume_subscription', { p_church: church.id }))}
          onCancel={() => run(() => supabase.rpc('cancel_subscription', { p_church: church.id }))}
        />
      )}

      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
